import { useEffect, Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider, useToast } from './components/Toast';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { ProtectedRoute } from './components/ProtectedRoute';
import { setToastCallback } from './lib/api-client';
import { AppLayout } from './components/AppLayout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { LandingPage } from './pages/LandingPage';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdateNotification from './components/PWAUpdateNotification';
import OfflineBanner from './components/OfflineBanner';

// Lazy load pages for code splitting
const LoginPage = lazy(() =>
  import('./pages/LoginPage').then((module) => ({ default: module.LoginPage }))
);
const SignupPage = lazy(() =>
  import('./pages/SignupPage').then((module) => ({ default: module.SignupPage }))
);
const TermsPage = lazy(() =>
  import('./pages/TermsPage').then((module) => ({ default: module.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((module) => ({ default: module.PrivacyPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage }))
);
const VerifyEmailPage = lazy(() =>
  import('./pages/VerifyEmailPage').then((module) => ({ default: module.VerifyEmailPage }))
);
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Transactions = lazy(() => import('./pages/Transactions'));
const NewTransaction = lazy(() => import('./pages/NewTransaction'));
const KYCPage = lazy(() => import('./pages/KYC').then((module) => ({ default: module.KYCPage })));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const Settings = lazy(() => import('./pages/Settings'));
const Documentation = lazy(() => import('./pages/Documentation'));
const TransactionDetail = lazy(() => import('./pages/TransactionDetail'));
const PaymentLinks = lazy(() => import('./pages/PaymentLinks'));
const PayPage = lazy(() => import('./pages/PayPage'));

function AppContent() {
  const { showToast } = useToast();

  // Set up the toast callback for the API client
  useEffect(() => {
    setToastCallback((type, message) => {
      showToast(type, message);
    });
  }, [showToast]);

  const page = (element: ReactNode) => <Suspense fallback={<LoadingSpinner />}>{element}</Suspense>;

  return (
    <Routes>
      <Route path="/" element={page(<LandingPage />)} />
      <Route path="/login" element={page(<LoginPage />)} />
      <Route path="/signup" element={page(<SignupPage />)} />
      <Route path="/terms" element={page(<TermsPage />)} />
      <Route path="/privacy" element={page(<PrivacyPage />)} />
      <Route path="/forgot-password" element={page(<ForgotPasswordPage />)} />
      <Route path="/verify-email" element={page(<VerifyEmailPage />)} />

      {/* Public pay page — no auth required */}
      <Route path="/pay/:slug" element={page(<PayPage />)} />

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={page(<Dashboard />)} />
        <Route path="transactions" element={page(<Transactions />)} />
        <Route path="transactions/new" element={page(<NewTransaction />)} />
        <Route path="transactions/:transactionId" element={page(<TransactionDetail />)} />
        <Route path="payment-links" element={page(<PaymentLinks />)} />
        <Route path="kyc" element={page(<KYCPage />)} />
        <Route path="api-keys" element={page(<ApiKeys />)} />
        <Route path="settings" element={page(<Settings />)} />
        <Route path="docs" element={page(<Documentation />)} />
      </Route>
      <Route path="/withdraw" element={<Navigate to="/transactions" replace />} />
      <Route path="/settlements" element={<Navigate to="/transactions" replace />} />
      <Route path="/documentation" element={<Navigate to="/docs" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
        <PWAUpdateNotification />
        <PWAInstallPrompt />
        <BrowserRouter>
          <GoogleAnalytics trackingId={import.meta.env.VITE_GA_TRACKING_ID} />
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
